const WARSAW_TIMEZONE = "Europe/Warsaw";
const PL_LOCALE = "pl-PL";

interface LocalDateTime {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

/**
 * Converts a UTC date string to an object with date/time parts in Europe/Warsaw timezone.
 * @returns {{ year, month, day, hour, minute, second }} or null
 */
export const convertToLocalDateTime = (
  utcDateString: string,
  timeZone: string = WARSAW_TIMEZONE
): LocalDateTime | null => {
  if (!utcDateString) return null;
  const date = new Date(utcDateString);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)!.value;
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
};

const dateTimeFormatter = new Intl.DateTimeFormat(PL_LOCALE, {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: WARSAW_TIMEZONE,
});

/**
 * Formats an ISO date string as a short date with time in Europe/Warsaw timezone (pl-PL locale).
 * @returns formatted string or placeholder if input is null
 */
export const formatToLocalDateTime = (
  isoString: string | null,
  placeholder = "—"
): string => {
  if (!isoString) return placeholder;
  return dateTimeFormatter.format(new Date(isoString));
};

const dateFormatter = new Intl.DateTimeFormat(PL_LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: WARSAW_TIMEZONE,
});

/**
 * Formats an ISO date string as DD.MM.YYYY in Europe/Warsaw timezone (pl-PL locale).
 * @returns formatted string or placeholder if input is null
 */
export const formatToLocalDate = (
  isoString: string | null,
  placeholder = "—"
): string => {
  if (!isoString) return placeholder;
  const parts = dateFormatter.formatToParts(new Date(isoString));
  const day = parts.find((p) => p.type === "day")!.value;
  const month = parts.find((p) => p.type === "month")!.value;
  const year = parts.find((p) => p.type === "year")!.value;
  return `${day}.${month}.${year}`;
};

/**
 * Returns the offset (in minutes) of `timeZone` at the given instant, i.e. how
 * far local wall-clock time is ahead of UTC (+120 for CEST, +60 for CET).
 * Native Intl only maps UTC→local, so we read the wall-clock parts the zone
 * would show for `date` and diff them against the same instant read as UTC.
 */
const getTimeZoneOffsetMinutes = (date: Date, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)!.value);
  // Wall-clock time the zone shows for this instant, interpreted as if it were UTC.
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") === 24 ? 0 : get("hour"),
    get("minute"),
    get("second")
  );
  return Math.round((asUtc - date.getTime()) / 60000);
};

/**
 * Serializes the first day of the given date's month at 01:00 Europe/Warsaw to
 * a UTC ISO string. Used for cyclic charge scheduling dates; the backend field
 * is a java.time.Instant, so the exact moment round-trips and the day/month is
 * resolved back in Warsaw time on the server.
 *
 * DST: 01:00 Warsaw maps to 00:00Z in winter (CET) and 23:00Z of the previous
 * day in summer (CEST) — both denote the same instant. The extra hour keeps the
 * value off the midnight boundary.
 */
export const toWarsawMonthStartUtcIso = (d: Date): string => {
  // Guess the UTC instant for 01:00 wall-clock, then correct by the zone offset
  // at that instant. First day at 01:00 is never inside a DST transition, so a
  // single correction is exact.
  const naiveUtc = Date.UTC(d.getFullYear(), d.getMonth(), 1, 1, 0, 0);
  const offsetMin = getTimeZoneOffsetMinutes(
    new Date(naiveUtc),
    WARSAW_TIMEZONE
  );
  return new Date(naiveUtc - offsetMin * 60000).toISOString();
};

const monthYearFormatter = new Intl.DateTimeFormat(PL_LOCALE, {
  month: "2-digit",
  year: "numeric",
  timeZone: WARSAW_TIMEZONE,
});

/**
 * Formats an ISO date string as MM.YYYY in Europe/Warsaw timezone.
 * @returns formatted string or placeholder if input is null
 */
export const formatToLocalMonthYear = (
  isoString: string | null,
  placeholder = "—"
): string => {
  if (!isoString) return placeholder;
  const parts = monthYearFormatter.formatToParts(new Date(isoString));
  const month = parts.find((p) => p.type === "month")!.value;
  const year = parts.find((p) => p.type === "year")!.value;
  return `${month}.${year}`;
};
